import { createApiClient } from '@platform/api-client';
import { env } from '@/shared/libs/env';
import {
  clearAuthStateCookie,
  hasAuthStateCookie,
} from '@/shared/libs/authState';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { API_ENDPOINTS } from '@/shared/constants/api/endpoints.ts';
import { REDIRECT_PARAM } from '@/shared/libs/redirect';
import { HTTP_STATUS } from '@/shared/constants/app/http.constant';
import { rememberOnboardingReturn } from '@/shared/libs/onboarding-return';

/** Gateway 가 약관 재동의 대상 토큰을 막을 때의 코드. */
const RECONSENT_REQUIRED_CODE = 'AUTH_RECONSENT_REQUIRED';

/**
 * 공용 API 클라이언트 (쿠키 기반 인증).
 *
 * ── 주의 ─────────────────────────────────────────────────────────────
 * VITE_API_URL 은 Gateway의 버전 prefix 를 포함하지 않도록 설정한다.
 *   예) VITE_API_URL=https://api.example.com
 * 각 호출부에서 `/v1/auth/login` 처럼 버전 prefix를 포함한 경로를 사용한다.
 *
 * ── 쿠키 모드 동작 ───────────────────────────────────────────────────
 * - withCredentials=true 가 자동 세팅됨
 * - 401 응답 시 다음 순서로 판단:
 *   1) `skipAuthRedirect: true` 옵션이면 → 그냥 401 reject (선택적 인증)
 *   2) auth_state 쿠키가 없으면 (= 비로그인) → 그냥 401 reject
 *   3) 위 둘 다 아니면 → /v1/auth/refresh 호출 → 성공시 원요청 재시도
 *   4) refresh 가 401/403 → onUnauthorized → 로그인 페이지로 하드 리다이렉트
 *      (네트워크·타임아웃·5xx 는 일시 장애이므로 세션을 유지한 채 reject)
 *
 * 핵심: 비로그인 상태에서 protected API를 호출해도 강제 /login 리다이렉트가
 * 발생하지 않는다. 비로그인 사용자가 들어갈 수 있는 public 페이지에서
 * 일부 API가 protected 여도 (e.g. 개인화 데이터) 페이지가 깨지지 않음.
 */
export const apiClient = createApiClient({
  baseURL: env.VITE_API_URL,
  authMode: 'cookie',
  refreshUrl: API_ENDPOINTS.auth.refresh,
  hasAuthHint: () => hasAuthStateCookie(),
  onUnauthorized: () => {
    clearAuthStateCookie();
    // 히스토리 대신 하드 리다이렉트로 앱 상태를 완전히 리셋
    // 현재 경로를 from 쿼리로 넘겨 로그인 후 복귀 가능하도록
    const from = window.location.pathname + window.location.search;
    const loginUrl =
      PATHS.LOGIN +
      (from && from !== '/'
        ? `?${REDIRECT_PARAM}=${encodeURIComponent(from)}`
        : '');

    window.location.href = loginUrl;
  },
  /**
   * 약관 재동의 대상 — 어떤 화면에서 막혀도 재동의 화면으로 보낸다(로그인 직후·AuthGuard 를 거치지 않고
   * 세션 도중에 개정이 시행된 경우). 동의 후 지금 화면으로 돌아오게 경로를 남긴다.
   */
  onServerError: ({ status, code }) => {
    if (status !== HTTP_STATUS.FORBIDDEN || code !== RECONSENT_REQUIRED_CODE) return;
    if (window.location.pathname === PATHS.RECONSENT) return;
    rememberOnboardingReturn(window.location.pathname + window.location.search);
    window.location.href = PATHS.RECONSENT;
  },
});
